import type { Dimension } from "@src/math/types";
import type { EigensystemResult } from "@src/math/matrix-analysis";
import type {
  EquationDiagnostic,
  EquationDiagnosticLocation,
  EquationDocument,
  EquationEvaluation,
  EquationMatrixCheckpoint,
  EvaluatedMatrix,
  EvaluatedVector,
  EvaluationResult,
  MatrixAnalysis,
} from "@src/features/equation-editor/equation";
import type { EvaluationWorkerResponse } from "./worker-messages";
import {
  matrixEntryIndex,
  matrixLabel,
  nodeId,
  vectorColor,
  vectorCoordinateIndex,
  vectorLabel,
} from "@src/features/equation-editor/equation-values";
import {
  matrix as toMatrix,
  transform,
  vector as toVector,
} from "@src/math/matrix";
import type { EquationExpressionLocation } from "./evaluation-engine";
import { matrixDurationMs } from "@src/features/equation-editor/equation-policy";

export function parseEvaluationWorkerResponse<D extends Dimension>(
  value: unknown,
  document: EquationDocument<D>,
): EvaluationWorkerResponse<D> {
  if (!isRecord(value) || typeof value.type !== "string")
    throw new Error("Invalid worker response");
  switch (value.type) {
    case "started":
    case "expression-complete":
    case "error":
      return { type: value.type };
    case "expression":
      return {
        type: value.type,
        location: parseExpressionLocation(value.location, document),
      };
    case "matrix":
      return {
        type: value.type,
        checkpoint: parseEvaluationCheckpoint(value.checkpoint, document),
      };
    case "result":
      return {
        type: value.type,
        result: parseEvaluationResult(value.result, document),
      };
    default:
      throw new Error("Invalid worker response");
  }
}

function parseEvaluationResult<D extends Dimension>(
  value: unknown,
  document: EquationDocument<D>,
): EvaluationResult<D> {
  if (!isRecord(value)) throw new Error("Invalid evaluation result");
  if (value.kind === "current") {
    const evaluation = parseEvaluation(value.value, document.dimension);
    assertTransformsAreFinite(evaluation);
    assertEvaluationMatchesDocument(evaluation, document);
    return {
      kind: "current",
      value: evaluation,
      matrixAnalysis: parseMatrixAnalysis(
        value.matrixAnalysis,
        document.dimension,
      ),
    };
  }
  if (value.kind !== "invalid" || !Array.isArray(value.diagnostics))
    throw new Error("Invalid evaluation result");
  const diagnostics = value.diagnostics.map((diagnostic) =>
    parseDiagnostic(diagnostic, document),
  );
  if (diagnostics.length === 0)
    throw new Error("Invalid evaluation diagnostics");
  const first = diagnostics[0];
  const checkpoint =
    value.checkpoint === undefined
      ? undefined
      : parseEvaluationCheckpoint(value.checkpoint, document);
  return {
    kind: "invalid",
    diagnostics: [first, ...diagnostics.slice(1)],
    ...(checkpoint === undefined ? {} : { checkpoint }),
  };
}

function parseEvaluation<D extends Dimension>(
  value: unknown,
  dimension: D,
): EquationEvaluation<D> {
  if (
    !isRecord(value) ||
    value.dimension !== dimension ||
    !Array.isArray(value.matrices) ||
    !Array.isArray(value.vectors)
  )
    throw new Error("Invalid equation evaluation");
  return {
    dimension,
    matrices: value.matrices.map((matrix) =>
      parseEvaluatedMatrix(matrix, dimension),
    ),
    vectors: value.vectors.map((vector) =>
      parseEvaluatedVector(vector, dimension),
    ),
    totalTransform: toMatrix(dimension, requireArray(value.totalTransform)),
  };
}

function parseEvaluatedMatrix<D extends Dimension>(
  value: unknown,
  dimension: D,
): EvaluatedMatrix<D> {
  if (!isRecord(value)) throw new Error("Invalid evaluated matrix");
  return {
    id: nodeId(value.id),
    label: matrixLabel(value.label),
    values: toMatrix(dimension, requireArray(value.values)),
    durationMs: matrixDurationMs(requireNumber(value.durationMs)),
  };
}

function parseEvaluatedVector<D extends Dimension>(
  value: unknown,
  dimension: D,
): EvaluatedVector<D> {
  if (!isRecord(value)) throw new Error("Invalid evaluated vector");
  return {
    id: nodeId(value.id),
    label: vectorLabel(value.label),
    coordinates: toVector(dimension, requireArray(value.coordinates)),
    color: vectorColor(value.color),
  };
}

function assertTransformsAreFinite<D extends Dimension>(
  evaluation: EquationEvaluation<D>,
): void {
  for (const vector of evaluation.vectors)
    transform(
      evaluation.dimension,
      evaluation.totalTransform,
      vector.coordinates,
    );
}

function assertEvaluationMatchesDocument<D extends Dimension>(
  evaluation: EquationEvaluation<D>,
  document: EquationDocument<D>,
): void {
  if (
    evaluation.matrices.length !== document.matrices.length ||
    evaluation.vectors.length !== document.vectors.length
  )
    throw new Error("Evaluation does not match equation document");
  for (let index = 0; index < document.matrices.length; index += 1) {
    const source = document.matrices[index];
    const evaluated = evaluation.matrices[index];
    if (
      source.id !== evaluated.id ||
      source.label !== evaluated.label ||
      source.durationMs !== evaluated.durationMs
    )
      throw new Error("Evaluation does not match equation document");
  }
  for (let index = 0; index < document.vectors.length; index += 1) {
    const source = document.vectors[index];
    const evaluated = evaluation.vectors[index];
    if (
      source.id !== evaluated.id ||
      source.label !== evaluated.label ||
      source.color !== evaluated.color
    )
      throw new Error("Evaluation does not match equation document");
  }
}

function parseEvaluationCheckpoint<D extends Dimension>(
  value: unknown,
  document: EquationDocument<D>,
): EquationMatrixCheckpoint<D> {
  if (!isRecord(value)) throw new Error("Invalid evaluation checkpoint");
  const checkpoint = {
    evaluation: parseEvaluation(value.evaluation, document.dimension),
    analysis: parseMatrixAnalysis(value.analysis, document.dimension),
  };
  if (
    checkpoint.evaluation.vectors.length !== 0 ||
    checkpoint.evaluation.matrices.length !== document.matrices.length
  )
    throw new Error("Invalid evaluation checkpoint");
  for (let index = 0; index < document.matrices.length; index += 1) {
    const source = document.matrices[index];
    const evaluated = checkpoint.evaluation.matrices[index];
    if (
      source.id !== evaluated.id ||
      source.label !== evaluated.label ||
      source.durationMs !== evaluated.durationMs
    )
      throw new Error("Invalid evaluation checkpoint");
  }
  return checkpoint;
}

function parseExpressionLocation<D extends Dimension>(
  value: unknown,
  document: EquationDocument<D>,
): EquationExpressionLocation {
  const location = parseLocation(value, document);
  if (location.index === undefined)
    throw new Error("Invalid expression location");
  return location.field === "matrix-entry"
    ? {
        nodeId: location.nodeId,
        field: location.field,
        index: matrixEntryIndex(document.dimension, location.index),
      }
    : {
        nodeId: location.nodeId,
        field: location.field,
        index: vectorCoordinateIndex(document.dimension, location.index),
      };
}

function parseMatrixAnalysis<D extends Dimension>(
  value: unknown,
  dimension: D,
): MatrixAnalysis<D> {
  if (!isRecord(value)) throw new Error("Invalid matrix analysis");
  return {
    inverseTransform: parseInverseTransform(value.inverseTransform, dimension),
    eigensystem: parseEigensystem(value.eigensystem, dimension),
  };
}

function parseInverseTransform<D extends Dimension>(
  value: unknown,
  dimension: D,
): MatrixAnalysis<D>["inverseTransform"] {
  if (!isRecord(value)) throw new Error("Invalid inverse transform");
  if (value.kind === "singular") return { kind: "singular" };
  if (value.kind !== "inverse") throw new Error("Invalid inverse transform");
  return {
    kind: "inverse",
    values: toMatrix(dimension, requireArray(value.values)),
  };
}

function parseEigensystem<D extends Dimension>(
  value: unknown,
  dimension: D,
): EigensystemResult<D> {
  if (!isRecord(value)) throw new Error("Invalid eigensystem");
  if (value.kind === "unavailable") return { kind: "unavailable" };
  if (
    value.kind !== "available" ||
    (value.spectrum !== "real" && value.spectrum !== "complex") ||
    !Array.isArray(value.realEigenvectors) ||
    value.realEigenvectors.length > dimension
  )
    throw new Error("Invalid eigensystem");
  return {
    kind: "available",
    spectrum: value.spectrum,
    realEigenvectors: value.realEigenvectors.map((vector) =>
      toVector(dimension, requireArray(vector)),
    ),
  };
}

function parseDiagnostic<D extends Dimension>(
  value: unknown,
  document: EquationDocument<D>,
): EquationDiagnostic {
  if (!isRecord(value) || typeof value.code !== "string")
    throw new Error("Invalid evaluation diagnostic");
  const location =
    value.location === undefined
      ? undefined
      : parseLocation(value.location, document);
  if (
    value.code === "invalid-expression" ||
    value.code === "expression-resource-limit"
  ) {
    if (location === undefined)
      throw new Error("Invalid evaluation diagnostic");
    return { code: value.code, location };
  }
  if (
    value.code !== "evaluation-timeout" &&
    value.code !== "evaluation-unavailable"
  )
    throw new Error("Invalid evaluation diagnostic");
  return {
    code: value.code,
    ...(location === undefined ? {} : { location }),
  };
}

function parseLocation<D extends Dimension>(
  value: unknown,
  document: EquationDocument<D>,
): EquationDiagnosticLocation {
  if (!isRecord(value)) throw new Error("Invalid diagnostic location");
  const id = nodeId(value.nodeId);
  const index = value.index;
  if (index !== undefined && (!Number.isInteger(index) || Number(index) < 0))
    throw new Error("Invalid diagnostic location");
  if (
    value.field === "matrix-entry" &&
    document.matrices.some((matrix) => matrix.id === id) &&
    (index === undefined || Number(index) < document.dimension ** 2)
  ) {
    return index === undefined
      ? { nodeId: id, field: value.field }
      : {
          nodeId: id,
          field: value.field,
          index: matrixEntryIndex(document.dimension, Number(index)),
        };
  }
  if (
    value.field === "vector-coordinate" &&
    document.vectors.some((vector) => vector.id === id) &&
    (index === undefined || Number(index) < document.dimension)
  ) {
    return index === undefined
      ? { nodeId: id, field: value.field }
      : {
          nodeId: id,
          field: value.field,
          index: vectorCoordinateIndex(document.dimension, Number(index)),
        };
  }
  throw new Error("Invalid diagnostic location");
}

function requireArray(value: unknown): unknown[] {
  if (!Array.isArray(value)) throw new Error("Invalid numeric tuple");
  return value;
}

function requireNumber(value: unknown): number {
  if (typeof value !== "number") throw new Error("Invalid numeric value");
  return value;
}

function isRecord(value: unknown): value is Record<string, unknown> {
  return typeof value === "object" && value !== null;
}
